/**
 * Seasonal offer banner (CNV-5973) — client side of snippets/offer-banner.liquid.
 * One controller per page; every banner instance shares the same campaign.
 *
 * - Gating: reveal only for Abra private-link sessions (?abralink= or the
 *   discount_code cookie containing the campaign keyword). Abra writes the
 *   cookie after landing, hence the short poll. Blank keyword = show to all.
 * - Abra: dispatch "abra:render" after theme price re-renders so dynamic
 *   pricing is re-applied. No-op without the embed.
 * - Pill: pinned to the advertised campaign % while armed (prices show the
 *   real charged amounts, percentages say the campaign number).
 * - Countdown to ends_at, one interval per page; banner hides at zero.
 * - Leaves: transforms/opacity only, paused off-screen, off under reduced motion.
 * - Coming-soon buy box (products without a plan) is revealed for armed sessions.
 */
(function () {
  'use strict';

  if (window.__lpOfferBanner) return;
  window.__lpOfferBanner = true;

  var banners = Array.prototype.slice.call(document.querySelectorAll('[data-offer-banner]'));
  if (!banners.length) return;

  var root = banners[0];
  var data = root.dataset;
  var endsAt = Date.parse(data.endsAt || '');
  var discountPct = parseFloat(data.discountPct) || 0;
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var armed = false;

  /* ---- Funnel gating ---------------------------------------------------- */

  var norm = function (s) {
    try { s = decodeURIComponent(s); } catch (e) { /* keep raw */ }
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  };
  var gateKey = norm(data.gateKeyword || '');

  var isArmed = function () {
    if (!gateKey) return true;
    try {
      var link = new URLSearchParams(location.search).get('abralink');
      if (link && norm(link).indexOf(gateKey) !== -1) return true;
    } catch (e) { /* URLSearchParams unavailable */ }
    var m = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
    return !!(m && norm(m[1]).indexOf(gateKey) !== -1);
  };

  /* ---- Abra render hook ------------------------------------------------- */

  var dispatchAbraRender = function () {
    if (window.Abra) window.dispatchEvent(new CustomEvent('abra:render'));
  };
  window.addEventListener('change', function (e) {
    if (e.target.closest && e.target.closest('.rtnu-product__form, purchase-type-selector')) {
      assertPill();
      dispatchAbraRender();
    }
  });
  window.addEventListener('purchase-type-change', dispatchAbraRender);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) dispatchAbraRender();
  });
  window.addEventListener('pageshow', dispatchAbraRender);

  /* ---- Subscribe & Save pill -------------------------------------------- */

  // Abra's rewrite would show the stacked total off list; re-assert the
  // campaign number through a guarded observer rather than setting it once.
  var pinPill = discountPct > 0;
  function assertPill() {
    if (!armed || !pinPill) return;
    var pct = discountPct + '%';
    document.querySelectorAll('.purchaseType-subscription__savings').forEach(function (el) {
      // Server markup nests the number in a __percentage span; the theme's
      // price JS later rewrites the whole span to flat "Save X%" text.
      var inner = el.querySelector('.purchaseType-subscription__percentage');
      if (inner) {
        if (inner.textContent.trim() !== pct) inner.textContent = pct;
      } else if (el.textContent.trim() !== 'Save ' + pct) {
        el.textContent = 'Save ' + pct;
      }
    });
  }
  if (pinPill) {
    var pillScope = document.querySelector('.rtnu-grid__right') || document.body;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var target = muts[i].target;
        var insideBanner = banners.some(function (b) { return b.contains(target); });
        if (!insideBanner) {
          assertPill();
          return;
        }
      }
    }).observe(pillScope, { subtree: true, childList: true, characterData: true });
  }

  /* ---- Countdown -------------------------------------------------------- */

  var timers = [];
  banners.forEach(function (b) {
    var t = b.querySelector('[data-offer-timer]');
    if (t) timers.push({ wrap: t, value: t.querySelector('[data-offer-countdown]') });
  });
  var timerInterval = null;
  var lastAriaMinute = -1;

  var split = function (ms) {
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60); s -= m * 60;
    return { d: d, h: h, m: m, s: s };
  };
  var format = function (ms) {
    var t = split(ms);
    return t.d > 0 ? t.d + 'd ' + t.h + 'h ' + t.m + 'm' : t.h + 'h ' + t.m + 'm ' + t.s + 's';
  };
  var plural = function (n, word) { return n + ' ' + word + (n === 1 ? '' : 's'); };
  var ariaLabel = function (ms) {
    var t = split(ms);
    var parts = t.d > 0
      ? [plural(t.d, 'day'), plural(t.h, 'hour')]
      : [plural(t.h, 'hour'), plural(t.m, 'minute')];
    return 'Offer ends in ' + parts.join(' and ');
  };

  var endOffer = function () {
    if (timerInterval) clearInterval(timerInterval);
    banners.forEach(function (b) { b.hidden = true; });
  };
  var tick = function () {
    var diff = endsAt - Date.now();
    if (diff <= 0) {
      endOffer();
      return;
    }
    var text = format(diff);
    var minute = Math.floor(diff / 60000);
    timers.forEach(function (t) {
      if (t.value.textContent !== text) t.value.textContent = text;
      if (minute !== lastAriaMinute) t.wrap.setAttribute('aria-label', ariaLabel(diff));
      t.wrap.hidden = false;
    });
    lastAriaMinute = minute;
  };
  var startTimer = function () {
    if (isNaN(endsAt) || !timers.length) return;
    tick();
    if (!timerInterval) timerInterval = setInterval(tick, 1000);
  };

  /* ---- Leaves ----------------------------------------------------------- */

  var SVG_NS = 'http://www.w3.org/2000/svg';
  // Maple leaf, 24×24, no stem. Swap for the prototype's path when the
  // handoff bundle (Offer Banner board.dc.html) is in hand.
  var LEAF_PATH = 'M12 2c.6 2 1.4 3.6 2.6 4.8l1.6-1.2-.4 3.4c1.4-.5 2.9-.6 4.4-.3L18.6 11l3.4 1.6-2.2 1.4 1.2 2.6c-1.8-.4-3.4-.4-4.8.2l.8 3.6-2.2-1.3L13 21.5v-4.2c-.4.1-.7.1-1 .1s-.6 0-1-.1v4.2l-1.8-2.4-2.2 1.3.8-3.6c-1.4-.6-3-.6-4.8-.2l1.2-2.6L2 12.6 5.4 11 3.8 8.7c1.5-.3 3-.2 4.4.3l-.4-3.4 1.6 1.2C10.6 5.6 11.4 4 12 2z';
  var DURATIONS = [8.3, 9.7, 10.9, 8.8, 11.6, 9.2, 10.3, 12.1];
  var SIZES = [11, 12.5, 14];
  var COLORS = ['var(--offer-leaf-a)', 'var(--offer-leaf-b)', 'var(--offer-leaf-c)'];
  var rand = function (min, max) { return min + Math.random() * (max - min); };

  var placeLeaf = function (leaf, rot) {
    leaf.style.left = rand(2, 96).toFixed(2) + '%';
    rot.style.transform = 'rotate(' + rand(-150, 150).toFixed(1) + 'deg)';
  };
  var buildLeaves = function (layer) {
    for (var i = 0; i < 12; i++) {
      var leaf = document.createElement('span');
      leaf.className = 'lp-offer__leaf';
      var sway = document.createElement('span');
      sway.className = 'lp-offer__leaf-sway';
      var rot = document.createElement('span');
      rot.className = 'lp-offer__leaf-rot';
      var svg = document.createElementNS(SVG_NS, 'svg');
      var path = document.createElementNS(SVG_NS, 'path');
      var size = SIZES[i % SIZES.length];
      var fall = DURATIONS[i % DURATIONS.length];

      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', size);
      svg.setAttribute('height', size);
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      path.setAttribute('d', LEAF_PATH);
      path.setAttribute('fill', COLORS[i % COLORS.length]);
      svg.appendChild(path);

      leaf.style.animationDuration = fall + 's';
      leaf.style.animationDelay = '-' + rand(0, 11).toFixed(2) + 's';
      sway.style.animationDuration = (2.6 + (i % 5) * 0.43).toFixed(2) + 's';
      svg.style.animationDuration = (fall / 2.6).toFixed(2) + 's';

      rot.appendChild(svg);
      sway.appendChild(rot);
      leaf.appendChild(sway);
      layer.appendChild(leaf);
      placeLeaf(leaf, rot);

      // Re-place while faded out at the end of each fall so the drift never
      // repeats. Sway and tilt iterations bubble too, so filter by name.
      leaf.addEventListener('animationiteration', (function (l, r) {
        return function (e) {
          if (e.target === l && e.animationName === 'lp-offer-fall') placeLeaf(l, r);
        };
      })(leaf, rot));
    }
  };
  var startLeaves = function () {
    if (data.animation !== 'leaves' || reducedMotion) return;
    banners.forEach(function (b) {
      var layer = b.querySelector('[data-offer-leaves]');
      if (!layer || layer.childElementCount) return;
      buildLeaves(layer);
    });
    var setPaused = function (b, paused) { b.classList.toggle('lp-offer--paused', paused); };
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          setPaused(entry.target, !entry.isIntersecting || document.hidden);
        });
      }, { threshold: 0 });
      banners.forEach(function (b) { io.observe(b); });
    }
    document.addEventListener('visibilitychange', function () {
      banners.forEach(function (b) {
        if (document.hidden) setPaused(b, true);
        else if (io) io.unobserve(b), io.observe(b);
        else setPaused(b, false);
      });
    });
  };

  /* ---- Coming-soon buy box ---------------------------------------------- */

  var revealGatedBuyBox = function () {
    document.querySelectorAll('.lp-offer-gated').forEach(function (el) { el.hidden = false; });
    document.querySelectorAll('.lp-offer-gated-fallback').forEach(function (el) { el.hidden = true; });
  };

  /* ---- Arm -------------------------------------------------------------- */

  var arm = function () {
    if (armed) return;
    if (!isNaN(endsAt) && endsAt <= Date.now()) return;
    armed = true;
    banners.forEach(function (b) { b.hidden = false; });
    revealGatedBuyBox();
    assertPill();
    startTimer();
    startLeaves();
    dispatchAbraRender();
  };

  if (isArmed()) {
    arm();
  } else {
    var tries = 0;
    var poll = setInterval(function () {
      if (isArmed()) {
        arm();
        clearInterval(poll);
      } else if (++tries >= 15) {
        clearInterval(poll);
      }
    }, 1000);
  }
})();
